import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import StudioHelpMermaid from './StudioHelpMermaid';
import StudioPageShell from './StudioPageShell';
import {
  STUDIO_HELP_SECTIONS,
  resolveHelpSection,
  slugifyHelpHeading,
} from '../../pages/studio/studioHelpCatalog';
import { recordFriendlinessEvent } from '../../utils/studioV2FriendlinessSignals';

const flattenChildrenText = (children) => (
  React.Children.toArray(children).map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (React.isValidElement(child)) return flattenChildrenText(child.props?.children);
    return '';
  }).join('')
);

const headingWithAnchor = (Tag, fallbackId) => ({ children }) => {
  const rawText = flattenChildrenText(children);
  const id = slugifyHelpHeading(rawText) || fallbackId;
  return <Tag id={id}>{children}</Tag>;
};

const markdownComponents = {
  a: ({ href, children }) => {
    const h = typeof href === 'string' ? href : '';
    if (!h) return <span>{children}</span>;
    if (/^https?:\/\//i.test(h)) {
      return (
        <a href={h} target="_blank" rel="noopener noreferrer">{children}</a>
      );
    }
    if (h.startsWith('/')) {
      return <RouterLink to={h}>{children}</RouterLink>;
    }
    if (h.startsWith('#')) {
      return <a href={h}>{children}</a>;
    }
    return <span title={h}>{children}</span>;
  },
  h1: headingWithAnchor('h1', 'section'),
  h2: headingWithAnchor('h2', 'subsection'),
  h3: headingWithAnchor('h3', 'subsubsection'),
  h4: headingWithAnchor('h4', 'subsubsection-deep'),
  code: ({ className, children }) => {
    const lang = /language-(\w+)/.exec(className || '')?.[1];
    const text = String(children).replace(/\n$/, '');
    if (lang === 'mermaid') {
      return <StudioHelpMermaid chart={text} />;
    }
    return <code className={className}>{children}</code>;
  },
  pre: ({ children }) => {
    const child = React.Children.toArray(children)[0];
    if (React.isValidElement(child) && child.props?.className?.includes('language-mermaid')) {
      return children;
    }
    return <pre>{children}</pre>;
  },
};

const markdownSx = {
  '& h1': { typography: 'h5', fontWeight: 700, mt: 0, mb: 1.5 },
  '& h2': {
    typography: 'h6', fontWeight: 700, mt: 3, mb: 1, pt: 1, borderTop: 1, borderColor: 'divider',
  },
  '& h2:first-of-type': { borderTop: 0, pt: 0, mt: 0 },
  '& h3': { typography: 'subtitle1', fontWeight: 700, mt: 2, mb: 0.75 },
  '& h4': { typography: 'subtitle2', fontWeight: 700, mt: 1.5, mb: 0.5 },
  '& blockquote': {
    m: 0, mb: 1.5, px: 1.5, py: 1, borderLeft: 3, borderColor: 'primary.main', bgcolor: 'action.hover', borderRadius: 1,
  },
  '& p': { typography: 'body2', mb: 1.25, color: 'text.secondary', lineHeight: 1.65 },
  '& li': { typography: 'body2', color: 'text.secondary', lineHeight: 1.6 },
  '& ul, & ol': { pl: 2.5, mb: 1.5 },
  '& hr': { border: 0, borderTop: 1, borderColor: 'divider', my: 2.5 },
  '& code': {
    fontFamily: 'monospace', fontSize: 12.5, bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5,
  },
  '& pre': {
    fontFamily: 'monospace', fontSize: 12, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto',
  },
  '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, typography: 'body2' },
  '& th, & td': { border: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
  '& th': { fontWeight: 600, bgcolor: 'action.hover' },
  '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
};

function HelpSidebar({ activeSectionId, activeHash }) {
  const onSectionNav = (path) => {
    recordFriendlinessEvent(`help_nav:${path}`);
  };

  const onAnchorNav = (anchorId) => {
    recordFriendlinessEvent(`help_anchor:${anchorId}`);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        position: { md: 'sticky' },
        top: { md: 16 },
        maxHeight: { md: 'calc(100vh - 120px)' },
        overflowY: { md: 'auto' },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
          Help topics
        </Typography>
      </Box>
      <List dense disablePadding>
        {STUDIO_HELP_SECTIONS.map((section) => {
          const selected = section.id === activeSectionId;
          return (
            <Box key={section.id}>
              <ListItemButton
                component={RouterLink}
                to={section.path}
                selected={selected}
                onClick={() => onSectionNav(section.path)}
                sx={{ py: 1, px: 2 }}
              >
                <ListItemText
                  primary={section.title}
                  secondary={selected ? section.description : null}
                  primaryTypographyProps={{ fontWeight: selected ? 700 : 600, fontSize: 14 }}
                  secondaryTypographyProps={{ fontSize: 12, sx: { mt: 0.25 } }}
                />
              </ListItemButton>
              {selected && section.toc?.length > 0 && (
                <List dense disablePadding sx={{ pb: 1, bgcolor: 'background.paper' }}>
                  {section.toc.map((item) => {
                    const href = `${section.path}#${item.id}`;
                    const anchorActive = activeHash === item.id;
                    return (
                      <ListItemButton
                        key={item.id}
                        component="a"
                        href={href}
                        selected={anchorActive}
                        onClick={() => onAnchorNav(item.id)}
                        sx={{ py: 0.5, pl: 3.5, pr: 2 }}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 13, color: anchorActive ? 'primary.main' : 'text.secondary' }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
              <Divider />
            </Box>
          );
        })}
      </List>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          component={RouterLink}
          to="/studio/templates"
          sx={{ textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
        >
          → Starter templates
        </Typography>
      </Box>
    </Paper>
  );
}

export default function StudioHelpLayout({ sectionId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const section = useMemo(() => resolveHelpSection(sectionId), [sectionId]);
  const activeHash = location.hash ? location.hash.replace(/^#/, '') : '';

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(activeHash);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.pathname, activeHash, section.id]);

  return (
    <StudioPageShell
      title={section.title}
      subtitle={section.description}
      backTo="/studio"
      backLabel="Studio"
      maxWidth={1200}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
          gap: { xs: 2, md: 3 },
          alignItems: 'start',
        }}
      >
        <HelpSidebar activeSectionId={section.id} activeHash={activeHash} />
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          {isMobile && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Pick a topic above, then use in-page links to jump within this section.
            </Typography>
          )}
          <Box sx={markdownSx}>
            <ReactMarkdown components={markdownComponents}>{section.markdown}</ReactMarkdown>
          </Box>
        </Paper>
      </Box>
    </StudioPageShell>
  );
}
